import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Stack, Table, TableBody,
    TableCell, TableRow, Typography,
} from '@mui/material';
import { useState } from 'react';
import { useHangoutHistory, useHangoutResults } from '../hooks/useHangout';
import { formatTokens } from '../lib/format';
import { colors, mono } from '../themes/theme';

/** Closed hangouts, each expanding to the standings frozen when it ended. */
export function PastHangouts() {
    const history = useHangoutHistory();
    const [open, setOpen] = useState<number | null>(null);

    const past = (history.data ?? []).filter((hangout) => hangout.status === 'closed');

    if (history.isPending) return <Typography color="text.secondary">Loading…</Typography>;

    if (past.length === 0) {
        return (
            <Typography color="text.secondary">
                Nothing finished yet. Closed hangouts show up here.
            </Typography>
        );
    }

    return (
        <Stack spacing={1}>
            {past.map((hangout) => (
                <Accordion
                    key={hangout.id}
                    expanded={open === hangout.id}
                    onChange={() => setOpen(open === hangout.id ? null : hangout.id)}
                    disableGutters
                    elevation={0}
                    sx={{
                        bgcolor: colors.surface,
                        border: `1px solid ${colors.line}`,
                        borderRadius: '3px',
                        '&::before': { display: 'none' },
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box>
                            <Typography sx={{ fontWeight: 700 }}>{hangout.name}</Typography>
                            <Typography
                                sx={{ fontFamily: mono, fontSize: '0.7rem', color: colors.muted }}
                            >
                                {hangout.closedAt?.toLocaleDateString()} ·{' '}
                                {hangout.players} in · {hangout.markets} bets
                                {hangout.winner && ` · won by ${hangout.winner}`}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                        {open === hangout.id && <Results hangoutId={hangout.id} />}
                    </AccordionDetails>
                </Accordion>
            ))}
        </Stack>
    );
}

function Results({ hangoutId }: { hangoutId: number }) {
    const results = useHangoutResults(hangoutId);

    if (results.isPending) return <Typography color="text.secondary">Loading…</Typography>;
    if (results.data?.length === 0) {
        return (
            <Typography variant="body2" sx={{ color: colors.muted }}>
                No standings were recorded for this one.
            </Typography>
        );
    }

    return (
        <Table size="small">
            <TableBody>
                {(results.data ?? []).map((row) => {
                    const net = row.finalBalance - row.startingStake;
                    return (
                        <TableRow key={row.userId}>
                            <TableCell
                                sx={{
                                    width: 36,
                                    fontFamily: mono,
                                    color: row.finishRank === 1 ? colors.safety : colors.muted,
                                    fontVariantNumeric: 'tabular-nums',
                                    border: 0,
                                }}
                            >
                                {row.finishRank}
                            </TableCell>
                            <TableCell sx={{ border: 0 }}>{row.handle}</TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    fontFamily: mono,
                                    fontVariantNumeric: 'tabular-nums',
                                    border: 0,
                                }}
                            >
                                {formatTokens(row.finalBalance)}
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    width: 76,
                                    fontFamily: mono,
                                    fontVariantNumeric: 'tabular-nums',
                                    color: net >= 0 ? colors.win : colors.loss,
                                    border: 0,
                                }}
                            >
                                {net >= 0 ? '+' : ''}
                                {formatTokens(net)}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}
